import React, { useEffect, useRef, useState } from 'react';

// Album images start before reaching the viewport. Keep a small preview visible
// while the display image loads; a failed request must never leave a broken img.
export default function ProgressivePhoto({ sources, preview, alt, eager = false, cropped = false, onOpen }) {
  const containerRef = useRef(null);
  const [active, setActive] = useState(eager);
  const [attempt, setAttempt] = useState(0);
  const [retry, setRetry] = useState(0);
  const [status, setStatus] = useState('loading');
  const [ratio, setRatio] = useState(1.5);
  const [previewReady, setPreviewReady] = useState(false);
  const source = sources[attempt];
  const requestSource = source && (retry ? `${source}${source.includes('?') ? '&' : '?'}galleryRetry=${retry}` : source);

  useEffect(() => {
    if (active) return undefined;
    if (!('IntersectionObserver' in window)) { setActive(true); return undefined; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setActive(true);
        observer.disconnect();
      }
    }, { rootMargin: '1200px 0px' });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [active]);

  useEffect(() => {
    if (!active || status !== 'loading') return undefined;
    if (!source) { setStatus('failed'); return undefined; }
    const timer = window.setTimeout(() => {
      if (attempt + 1 < sources.length) setAttempt(value => value + 1);
      else setStatus('failed');
    }, 12000);
    return () => window.clearTimeout(timer);
  }, [active, status, source, attempt, sources.length, retry]);

  useEffect(() => {
    const recover = () => {
      if (status !== 'failed') return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.bottom < -1200 || rect.top > window.innerHeight + 1200) return;
      setAttempt(0); setStatus('loading'); setRetry(value => value + 1);
    };
    window.addEventListener('online', recover);
    return () => window.removeEventListener('online', recover);
  }, [status]);

  const rememberRatio = image => {
    if (image.naturalWidth && image.naturalHeight) setRatio(image.naturalWidth / image.naturalHeight);
  };
  const fail = () => {
    if (attempt + 1 < sources.length) setAttempt(value => value + 1);
    else setStatus('failed');
  };
  const tryAgain = () => { setAttempt(0); setStatus('loading'); setRetry(value => value + 1); };
  return <div ref={containerRef} className="progressive-photo" data-load-state={active ? status : 'waiting'} style={{ aspectRatio: cropped ? '4 / 3' : ratio }}>
    <button type="button" className="progressive-photo-open" onClick={onOpen} aria-label={`檢視照片：${alt}`}>
      {active && preview && <img className="progressive-photo-preview" src={preview} alt="" decoding="async" style={{ opacity: previewReady ? 1 : 0, objectFit: cropped ? 'cover' : 'contain' }} onLoad={event => { if (status !== 'ready') rememberRatio(event.currentTarget); setPreviewReady(true); }} onError={() => setPreviewReady(false)} />}
      {active && source && status !== 'failed' && <img key={`${attempt}-${retry}`} src={requestSource} alt={alt} decoding="async" fetchpriority={eager ? 'high' : 'auto'} className="progressive-photo-full" style={{ opacity: status === 'ready' ? 1 : 0, objectFit: cropped ? 'cover' : 'contain' }} onLoad={event => { rememberRatio(event.currentTarget); setStatus('ready'); }} onError={fail} />}
    </button>
    {active && status === 'loading' && <span className="progressive-photo-status" role="status">{previewReady ? '正在載入清晰照片…' : '照片載入中…'}</span>}
    {active && (status === 'failed' || (status === 'ready' && attempt > 0)) && <button type="button" className="progressive-photo-retry" onClick={tryAgain}>{status === 'failed' ? '載入未完成，點此重試' : '已顯示備用照片 · 重試清晰版'}</button>}
  </div>;
}
